import {
  LeaseFromInfoFormValues,
  LeaseToInfoFormValues,
  OwnerInfoFormValues,
  VehicleOwnershipType,
} from "../types/ownership";
import {
  PaginatedList,
  PaginatedQueryParams,
  PaginationParams,
} from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

// Re-export for backward compatibility
export { VehicleOwnershipType };

// New ownership-related DTOs
export interface LessorDto {
  id: number;
  name: string;
}

export interface LesseeDto {
  id: number;
  name: string;
}

// Use shared ownership DTOs
export interface VehicleOwnerInfoDto extends OwnerInfoFormValues {}
export interface VehicleLeaseFromDto extends LeaseFromInfoFormValues {}
export interface VehicleLeaseToDto extends LeaseToInfoFormValues {}

export interface Vehicle {
  id: number;
  externalId: string | null;
  telematicsProvider: string | null;
  name: string;
  registrationNumber: string | null;
  vin: string | null;
  type: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastSpeed: number | null;
  lastHeading: number | null;
  lastStatus: string;
  lastLocationUpdate: string | null;
  lastAddress?: string | null;
  dataSource: string;
  createdAt: string;
  updatedAt: string | null;
  serial: string | null;
  gatewayHealthStatus: string | null;
  gatewayLastConnected: string | null;
  gatewayUnplugDetectedAt: string | null;
  isArchived: boolean;
  archivedAt: string | null;
  // Fault related fields
  lastFaultTime: string | null;
  lastFaultCanBusType: string | null;
  lastFaultSpnDescription: string | null;
  lastFaultFmiDescription: string | null;
  lastDtcDescription: string | null;
  lastDtcCode: string | null;
  lastEmissionsLightOn: boolean;
  lastProtectLightOn: boolean;
  lastStopLightOn: boolean;
  lastWarningLightOn: boolean;
  lastCheckEngineLightOn: boolean;
}

// Lookup DTOs matching backend
export interface VehicleStatusDto {
  id: number;
  name: string;
}

export interface VehicleTypeDto {
  id: number;
  name: string;
}

export interface DivisionDto {
  id: number;
  name: string;
}

// API Response type is now imported from utils/apiUtils.ts

// Detailed Vehicle Response DTO matching VehicleResponseDto
export interface VehicleDetailedResponseData {
  id: number;
  externalId: string;
  name: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  serial: string;
  cameraSerial: string;
  gatewaySerial: string;
  gatewayModel: string;
  gatewayHealthStatus: string | null;
  gatewayLastConnected: string | null;
  gatewayUnplugDetectedAt: string | null;
  harshAccelerationSettingType: string;
  vehicleRegulationMode: string;
  notes: string;
  isArchived: boolean;
  archivedAt: string | null;
  createdInSamsara: string;
  updatedInSamsara: string;
  createdAt: string;
  updatedAt: string | null;
  dataSource: string;
  telematicsProvider: string | null;

  // Latest location data
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastSpeed: number | null;
  lastHeading: number | null;
  lastStatus: string;
  lastLocationUpdate: string | null;

  // Latest fault data
  lastFaultCanBusType: string;
  lastFaultTime: string | null;
  lastEmissionsLightOn: boolean;
  lastProtectLightOn: boolean;
  lastStopLightOn: boolean;
  lastWarningLightOn: boolean;
  lastCheckEngineLightOn: boolean;
  lastFaultSpnDescription: string;
  lastFaultFmiDescription: string;
  lastDtcDescription: string;
  lastDtcCode: string;
  lastFaultSpn: string;
  lastFaultFmi: string;
  lastFaultMilStatus: boolean;
  lastFaultSourceComponent: string;
  lastFaultSeverity: string;

  // General Section (from VehicleGeneral)
  unitNumber: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  vehicleType: string;
  vehicleStatus: string;
  division: string;

  // Truck Info Section (from VehicleInfo)
  color: string;
  licensePlate: string;
  licenseState: string;
  fuelCardNumber: string;
  fuelType: string;
  iPassNumber: string;
  bestPassNumber: string;
  prePassEzPassNumber: string;
  tireSize: string;
  deductTolls: boolean;
  deductFuel: boolean;

  // Ownership Section (from VehicleOwnership) - backward compatibility
  isCompanyOwned: boolean;
  isLeasedFrom: boolean;
  isLeasedTo: boolean;

  // New ownership structure
  ownershipType: VehicleOwnershipType;
  ownerInfo: VehicleOwnerInfoDto | null;
  leaseFromInfo: VehicleLeaseFromDto | null;
  leaseToInfo: VehicleLeaseToDto | null;

  // Safety Section (from VehicleSafety)
  dotInspectionExpiration: string | null;
  platesExpirationDate: string | null;
  insuranceExpirationDate: string | null;
  insuranceValue: number | null;
  escrowStartingBalance: number | null;
  escrowAccountBalance: number | null;
  hasCustomWarning: boolean;

  // IFTA Section (from VehicleIfta)
  iftaHandledByCompany: boolean;
  iftaExpirationDate: string | null;
  nyPermitNumber: string;
  grossWeight: number | null;
  unloadedWeight: number | null;
  numberOfAxles: number | null;

  // Dispatch Section (from VehicleDispatch)
  dispatcher: string;
  dispatchGroup: string;
  dispatchCategory: string;

  // GPS Section (from VehicleGps)
  gpsType: string;

  // Odometer Section (from VehicleOdometer)
  lastOdometerReading: number | null;
  lastOdometerDate: string | null;
  estimatedOdometer: number | null;

  // Comments Section (from VehicleComments)
  comments: string;

  // Available lookup data for dropdowns
  availableVehicleStatuses: VehicleStatusDto[];
  availableVehicleTypes: VehicleTypeDto[];
  availableDivisions: DivisionDto[];
  availableLessors: LessorDto[];
  availableLessees: LesseeDto[];
}

// Update Request DTO matching UpdateVehicleRequestDto
export interface UpdateVehicleRequest {
  // General Section (from VehicleGeneral)
  unitNumber: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;

  // Lookup fields - either use existing ID or create new from name
  vehicleTypeId: number | null;
  vehicleTypeName: string | null;
  vehicleStatusId: number | null;
  vehicleStatusName: string | null;
  divisionId: number | null;
  divisionName: string | null;

  // Truck Info Section (from VehicleInfo)
  color: string;
  licensePlate: string;
  licenseState: string;
  fuelCardNumber: string;
  fuelType: string;
  iPassNumber: string;
  bestPassNumber: string;
  prePassEzPassNumber: string;
  tireSize: string;
  deductTolls: boolean;
  deductFuel: boolean;

  // Ownership Section (from VehicleOwnership) - NEW STRUCTURE
  ownershipType: VehicleOwnershipType;
  ownerInfo: VehicleOwnerInfoDto | null;
  leaseFromInfo: VehicleLeaseFromDto | null;
  leaseToInfo: VehicleLeaseToDto | null;

  // Safety Section (from VehicleSafety)
  dotInspectionExpiration: string | null;
  platesExpirationDate: string | null;
  insuranceExpirationDate: string | null;
  insuranceValue: number | null;
  escrowStartingBalance: number | null;
  escrowAccountBalance: number | null;
  hasCustomWarning: boolean;

  // IFTA Section (from VehicleIfta)
  iftaHandledByCompany: boolean;
  iftaExpirationDate: string | null;
  nyPermitNumber: string;
  grossWeight: number | null;
  unloadedWeight: number | null;
  numberOfAxles: number | null;

  // Dispatch Section (from VehicleDispatch)
  dispatcher: string;
  dispatchGroup: string;
  dispatchCategory: string;

  // GPS Section (from VehicleGps)
  gpsType: string;

  // Odometer Section (from VehicleOdometer)
  lastOdometerReading: number | null;
  lastOdometerDate: string | null;
  estimatedOdometer: number | null;

  // Comments Section (from VehicleComments)
  comments: string;

  name: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: string;
}

export type CreateVehicleRequest = UpdateVehicleRequest & {
  vin: string;
};

export interface VehicleLocation {
  vehicleId: number;
  vehicleName: string;
  latitude: number;
  longitude: number;
  speed: number;
  status: string;
  timestamp: string;
  dataSource: string;
  heading?: number;
  formattedAddress?: string | null;
}

export interface GetVehicleLocationHistoryRequest {
  vehicleId: number;
  limit?: number;
  daysBack?: number;
}

export interface VehicleWithHistory extends Vehicle {
  previousLocation?: {
    latitude: number;
    longitude: number;
    heading?: number;
    timestamp: string;
  };
}

export interface HosDriverProfileDto {
  id: number;
  driverName: string;
  driverId: string;
  evaEldVehicleId: string;
  breakTime: number;
  driveTime: number;
  shiftTime: number;
  cycleTime: number;
  cycleTomorrowTime: number;
  dutyStatus: string;
  vehicleId: number | null;
  /** Matched Employee.PhoneNumber by name + tenant */
  phoneNumber?: string | null;
  lastSyncTime: string;
}

export interface HosStatusSummary {
  status: string;
  count: number;
  totalTime: number;
}

export interface VehicleFaultEventDto {
  id: number;
  vehicleId: number;
  vehicleName: string;
  spnId: number;
  spnDescription: string;
  fmiId: number;
  fmiDescription: string;
  milStatus: boolean;
  sourceAddressName: string;
  status: string;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrenceCount: number;
  severity: string;
  canBusType: string;
  dtcCode: string;
  dtcDescription: string;
  faultType: string;
  txId: number;
  emissionsLightOn: boolean;
  protectLightOn: boolean;
  stopLightOn: boolean;
  warningLightOn: boolean;
  checkEngineLightOn: boolean;
}

export const vehiclesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getVehicles: builder.query<Vehicle[], void>({
      query: () => "vehicles",
      transformResponse: unwrapApiResponse,
      providesTags: ["Vehicles"],
    }),
    getVehiclesPaginated: builder.query<
      PaginatedList<Vehicle>,
      PaginationParams
    >({
      query: (params) => ({
        url: "vehicles",
        params: { ...params, paginated: true },
      }),
      transformResponse: unwrapApiResponse,
      providesTags: ["Vehicles"],
    }),
    getVehiclesPaginatedWithSearch: builder.query<
      PaginatedList<Vehicle>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
          paginated: true,
        };

        // Add filters as separate query parameters: filters[key]=value
        if (
          queryParams.filters &&
          Object.keys(queryParams.filters).length > 0
        ) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              params[`filters[${key}]`] = value;
            }
          });
        }

        return {
          url: "vehicles",
          params,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["Vehicles"],
    }),
    getVehicleById: builder.query<Vehicle, number>({
      query: (id) => `vehicles/${id}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, id) => [{ type: "Vehicles", id }],
    }),
    // New endpoint for getting vehicle with comprehensive details
    getVehicleWithDetails: builder.query<VehicleDetailedResponseData, number>({
      query: (id) => `vehicles/${id}/details`,
      providesTags: (result, error, id) => [{ type: "Vehicles", id }],
      transformResponse: unwrapApiResponse,
    }),
    getVehicleLocationHistory: builder.query<
      VehicleLocation[],
      GetVehicleLocationHistoryRequest
    >({
      query: ({ vehicleId, limit = 100, daysBack = 1 }) =>
        `vehicles/${vehicleId}/history?limit=${limit}&daysBack=${daysBack}`,
      transformResponse: unwrapApiResponse,
    }),
    getAllHosProfiles: builder.query<HosDriverProfileDto[], void>({
      query: () => "hos",
      transformResponse: unwrapApiResponse,
      providesTags: ["HosProfiles"],
    }),
    getHosProfilesPaginated: builder.query<
      PaginatedList<HosDriverProfileDto>,
      PaginationParams
    >({
      query: (params) => ({
        url: "hos/paginated",
        params: { ...params },
      }),
      transformResponse: unwrapApiResponse,
      providesTags: ["HosProfiles"],
    }),
    getHosProfilesPaginatedWithSearch: builder.query<
      PaginatedList<HosDriverProfileDto>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
        };

        if (
          queryParams.filters &&
          Object.keys(queryParams.filters).length > 0
        ) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              params[`filters[${key}]`] = value;
            }
          });
        }

        return {
          url: "hos/paginated",
          params,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["HosProfiles"],
    }),
    createVehicle: builder.mutation<VehicleDetailedResponseData, CreateVehicleRequest>({
      query: (vehicle) => ({
        url: "vehicles",
        method: "POST",
        body: vehicle,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["Vehicles"],
    }),
    updateVehicle: builder.mutation<Vehicle, Vehicle>({
      query: (vehicle) => ({
        url: `vehicles/${vehicle.id}`,
        method: "PUT",
        body: vehicle,
      }),
      invalidatesTags: (result, error, vehicle) => [
        "Vehicles",
        { type: "Vehicles", id: vehicle.id },
      ],
    }),
    // New endpoint for updating vehicle with comprehensive details
    updateVehicleWithDetails: builder.mutation<
      VehicleDetailedResponseData,
      { id: number; vehicle: UpdateVehicleRequest }
    >({
      query: ({ id, vehicle }) => ({
        url: `vehicles/${id}`,
        method: "PUT",
        body: vehicle,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result, error, { id }) => [
        "Vehicles",
        { type: "Vehicles", id },
      ],
    }),
    getFaultEventsPaginated: builder.query<
      PaginatedList<VehicleFaultEventDto>,
      PaginationParams
    >({
      query: (params) => ({
        url: "vehicleFaultEvent",
        params: { ...params },
      }),
      transformResponse: unwrapApiResponse,
      providesTags: ["VehicleFaults"],
    }),
    getFaultEventsPaginatedWithSearch: builder.query<
      PaginatedList<VehicleFaultEventDto>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
        };

        // Add filters as separate query parameters: filters[key]=value
        if (
          queryParams.filters &&
          Object.keys(queryParams.filters).length > 0
        ) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              params[`filters[${key}]`] = value;
            }
          });
        }

        return {
          url: "vehicleFaultEvent",
          params,
        };
      },
      transformResponse: unwrapApiResponse,
      providesTags: ["VehicleFaults"],
    }),
    getFaultEventById: builder.query<VehicleFaultEventDto, number>({
      query: (id) => `vehicleFaultEvent/${id}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, id) => [{ type: "VehicleFaults", id }],
    }),
    getFaultEventsByVehicleId: builder.query<VehicleFaultEventDto[], number>({
      query: (vehicleId) => `vehicleFaultEvent/vehicle/${vehicleId}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, vehicleId) => [
        { type: "VehicleFaults", id: `vehicle-${vehicleId}` },
      ],
    }),
    getActiveFaultEvents: builder.query<VehicleFaultEventDto[], void>({
      query: () => "vehicleFaultEvent/active",
      transformResponse: unwrapApiResponse,
      providesTags: ["VehicleFaults"],
    }),
    archiveVehicle: builder.mutation<void, number>({
      query: (id) => ({
        url: `vehicles/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: ["Vehicles", "Documents"],
    }),
    restoreVehicle: builder.mutation<void, number>({
      query: (id) => ({
        url: `vehicles/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: ["Vehicles", "Documents"],
    }),
  }),
});

export const {
  useGetVehiclesQuery,
  useGetVehiclesPaginatedQuery,
  useGetVehiclesPaginatedWithSearchQuery,
  useGetVehicleByIdQuery,
  useGetVehicleWithDetailsQuery,
  useGetVehicleLocationHistoryQuery,
  useGetAllHosProfilesQuery,
  useGetHosProfilesPaginatedQuery,
  useGetHosProfilesPaginatedWithSearchQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useUpdateVehicleWithDetailsMutation,
  useGetFaultEventsPaginatedQuery,
  useGetFaultEventsPaginatedWithSearchQuery,
  useGetFaultEventByIdQuery,
  useGetFaultEventsByVehicleIdQuery,
  useGetActiveFaultEventsQuery,
  useArchiveVehicleMutation,
  useRestoreVehicleMutation,
} = vehiclesApi;
